// Accepted answers for the REVERSE drill — the word is shown, its MEANING is typed — in ANY
// language pair (design: docs/design/de-en-reverse-answer-matching.md, generalized 2026-08-03).
// Stored translations are curated display strings ("to move (house)", "they, you went",
// "бросать (кого-л.)"); this module expands each into every spelling a correct answer may
// reasonably be typed as, and provides the comparison key both sides go through.
//
// The core is language-NEUTRAL on purpose: splitting alternatives, inlining or dropping
// parenthetical qualifiers, collapsing whitespace and ignoring case and terminal punctuation are
// grammar-free string work. Whatever a particular answer language needs on top lives in ONE
// RULES row, and every rule in a row only ever ADDS accepted spellings — so a language with no
// row at all still drills reverse perfectly well, it just asks for the translation as curated.
// Adding tolerance for a language is adding a row here; nothing else in the app carries a
// language list for the direction. Reverse is ALWAYS case-insensitive: strict capitalisation is
// a German-orthography setting with no counterpart on the answer side.
//
// Pure string -> array, no DOM access; used by training_session.js the same way acceptedForms()
// serves the forward direction.
var AnswerForms = (function () {
	"use strict";

	// кого-л. / кого-либо / кого-нибудь all fill the same slot and every dictionary picks a
	// different suffix — canonicalized onto "-либо" so a stored form and a typed one meet.
	// Deliberately NOT including "-то": что-то / кто-то are ordinary words too, and folding them
	// would accept answers that differ in meaning.
	function ruPlaceholders() {
		var stems = ["кто", "кого", "кому", "кем", "ком",
			"что", "чего", "чему", "чем", "чём", "где", "куда"];
		var suffixes = ["-л.", "-л", "-либо", "-нибудь"];
		var map = {};
		for (var i = 0; i < stems.length; i++) {
			for (var j = 0; j < suffixes.length; j++) {
				map[stems[i] + suffixes[j]] = stems[i] + "-либо";
			}
		}
		return map;
	}

	// Combining acute and grave — the Russian stress mark. Built from char codes rather than
	// written into a regex literal on purpose: as source characters they are invisible, and an
	// editor or a copy-paste that drops them would silently disable the fold below.
	var RU_STRESS = new RegExp("[" + String.fromCharCode(0x300, 0x301) + "]", "g");

	// Per-ANSWER-LANGUAGE tolerance. `placeholders` canonicalizes interchangeable slot words on
	// BOTH sides of the comparison (see key()); `optionalLeading` lists prefixes a correct answer
	// may omit, applied in order and chained (each pattern also runs over what the earlier ones
	// produced); `fold` normalizes letters a typist substitutes freely. All three are optional.
	var RULES = {
		// English: the infinitive marker and the articles are optional ("to resign" accepts
		// "resign"; "to the trains" accepts "trains"), and the corpus's sb/sth shorthand is
		// interchangeable with the spelled-out words. Kept consistent with the Java distractor
		// guard's stopword vocabulary (LearningTextSupport.STOPWORDS) — same family, different
		// purpose.
		EN: {
			placeholders: {
				"sb": "someone", "sb.": "someone", "somebody": "someone", "so.": "someone",
				"sth": "something", "sth.": "something", "smth": "something", "smth.": "something"
			},
			optionalLeading: [/^to\s+(.+)$/i, /^(?:the|an?)\s+(.+)$/i]
		},
		// Russian: no articles and no infinitive particle, so nothing is optional up front. What
		// does vary is the placeholder suffix the dictionary picked; ё — which most typists enter
		// as е, on a keyboard where the two keys are nowhere near each other; and the STRESS MARK.
		// Russian dictionaries mark stress and this corpus does too («живо́тик», «временны́е рамки» —
		// 18,367 stored strings across DE→RU and EN→RU), but the mark is a combining acute that no
		// learner has a key for: without stripping it the only spelling a human can type grades
		// wrong. Stripped via NFD so the precomposed ѐ/ѝ fold the same way as the combining form,
		// and ё is recomposed first so its own rule still fires.
		RU: {
			placeholders: ruPlaceholders(),
			fold: function (s) {
				return s.normalize("NFD").replace(RU_STRESS, "")
					.normalize("NFC").replace(/ё/g, "е");
			}
		}
	};

	// A language with no row drills reverse on the neutral core alone. This is what makes the
	// feature work for a pair nobody has written rules for yet.
	var DEFAULT = { placeholders: {}, optionalLeading: [], fold: null };

	function rulesFor(lang) {
		return RULES[String(lang || "").toUpperCase()] || DEFAULT;
	}

	// Runaway-expansion backstop (slash × comma × parens × attached endings). Raised 24 → 72 on
	// 2026-08-27 together with the "keep the curated string" and "attach bracketed endings" fixes,
	// both of which multiply forms. Measured over all 331,624 stored translations in the four
	// pairs (temp/mockups/cap_sweep.mjs): the worst real string expands to 69 forms, so 72 is the
	// first value that truncates NOTHING — 36 truncates 10 strings and silently drops 17 answers
	// that graded right before. Truncation is invisible to the user by construction, which is why
	// the backstop sits above the real maximum rather than near it. Re-measure before adding
	// another expansion step.
	var MAX_FORMS_PER_TRANSLATION = 72;

	function collapse(s) {
		return s.replace(/\s+/g, " ").trim();
	}

	// Comparison key — the reverse-direction twin of training_session.js looseKey():
	// apostrophes dropped, whitespace collapsed, always lowercased, then the language's own folding
	// and placeholder canonicalization, and finally every remaining run of non-letters folded to a
	// single space so only the WORDS are graded. Both the accepted forms and the typed attempt go
	// through this before comparing. Fold runs before AND after the placeholder pass so a folded
	// lookup can't produce an unfolded canonical form.
	//
	// The separator fold comes LAST on purpose: the placeholder maps are keyed on words that carry
	// their own punctuation ("sth.", "кого-л."), so folding first would look up "кого л" and quietly
	// lose every canonicalization this module does. Running it afterwards leaves both sides of the
	// comparison equally folded ("кого-либо" and "кого либо" meet) and adds nothing else.
	function key(s, lang) {
		var rules = rulesFor(lang);
		var k = collapse(s)
			.replace(/['‘’ʼ]/g, "")
			.replace(/[.!?…]+$/, "")
			.trim()
			.toLowerCase();
		if (rules.fold) { k = rules.fold(k); }
		var placeholders = rules.placeholders || {};
		var words = k.split(" ");
		for (var i = 0; i < words.length; i++) {
			if (Object.prototype.hasOwnProperty.call(placeholders, words[i])) {
				words[i] = placeholders[words[i]];
			}
		}
		k = words.join(" ");
		if (rules.fold) { k = rules.fold(k); }
		return k.replace(/[^\p{L}\p{M}\p{N}]+/gu, " ").trim();
	}

	// Split on a separator at paren depth 0 only — "to spill (tears, blood)" must NOT split
	// on the comma inside the qualifier. sep is ", " or " / " (checked at the exact position).
	function topLevelSplit(s, sep) {
		var parts = [];
		var depth = 0, start = 0;
		for (var i = 0; i < s.length; i++) {
			var ch = s.charAt(i);
			if (ch === "(") depth++;
			else if (ch === ")") depth = Math.max(0, depth - 1);
			else if (depth === 0 && s.substr(i, sep.length) === sep) {
				parts.push(s.substring(start, i));
				start = i + sep.length;
				i += sep.length - 1;
			}
		}
		parts.push(s.substring(start));
		return parts;
	}

	// Variant-compact ENDINGS (word-form-entries thin rows): «играл, -а, -о, -и» packs four
	// grammatical variants into one string, and a "-а" segment is a morphological ending of the
	// last full segment, never a word of its own — «играла» must grade right, bare «а» must not.
	// Each ending expands against the base segment: attached to every word one at a time, and
	// (for a multi-word base like «был обязан, -а») to every consonant-final word at once, since
	// Russian inflects both halves together («была обязана»). Adjective endings (-ая/-ое/-ые on a
	// base ending in -ый/-ий/-ой) REPLACE that suffix instead of appending — the standard
	// dictionary compaction. The letter tests are all Cyrillic-shaped and simply never fire on a
	// language that doesn't write endings this way, which keeps the core language-neutral.
	var ENDING_SEG = /^[-‑]\s?([\p{L}]{1,4})$/u;

	function attachEnding(word, ending) {
		if (/[ыио]й$/i.test(word) && /^(ая|яя|ое|ее|ые|ие)$/i.test(ending)) {
			return word.slice(0, -2) + ending;
		}
		return word + ending;
	}

	function expandEndings(segments) {
		var out = [];
		var base = null;
		for (var i = 0; i < segments.length; i++) {
			var seg = collapse(segments[i]);
			if (!seg) continue;
			var m = ENDING_SEG.exec(seg);
			if (m && base) {
				var ending = m[1];
				var words = base.split(" ");
				var variants = {};
				for (var w = 0; w < words.length; w++) {
					var v = words.slice();
					v[w] = attachEnding(v[w], ending);
					variants[v.join(" ")] = true;
				}
				if (words.length > 1) {
					var all = words.map(function (bw) {
						return /[^аеёиоуыэюя]$/i.test(bw) ? attachEnding(bw, ending) : bw;
					}).join(" ");
					variants[all] = true;
				}
				for (var k in variants) {
					if (Object.prototype.hasOwnProperty.call(variants, k)) out.push(k);
				}
			} else if (!m) {
				base = seg;
				out.push(seg);
			}
			// an ending with no base yet ("-а" first in the list) is dropped — it is not a word
		}
		return out;
	}

	// Comma alternatives may share their tail: "they, you went" is "they went" + "you went".
	// A short segment (fewer words than the last one) gets the last segment's trailing words —
	// but the segment AS WRITTEN is always accepted too. Both, because tail-sharing is a habit of
	// curated sense translations, while a legacy definition string is a flat list ("ее, ей, на
	// нее"): completing there would turn "ей" into the nonsense "ей нее" and — worse — reject the
	// bare "ей", which is a perfectly correct answer.
	function completeWithTail(segments) {
		var out = [];
		// A `; `-group that is NOTHING but endings («-ка», «-est», «широко расставив ноги; -ая,
		// -ое, -ие» — the second group) leaves expandEndings with no base and an empty list. There
		// is no tail to complete against, and no forms to complete: expandOne's
		// `return out.length ? out : [t]` then falls back to the literal string, which is exactly
		// right for a legitimate suffix entry. Without this guard the read below is `undefined`
		// and accepted() throws a TypeError in the answer path.
		if (!segments.length) return out;
		var lastWords = collapse(segments[segments.length - 1]).split(" ");
		for (var i = 0; i < segments.length; i++) {
			var seg = collapse(segments[i]);
			if (!seg) continue;
			var words = seg.split(" ");
			if (i < segments.length - 1 && words.length < lastWords.length) {
				out.push(seg + " " + lastWords.slice(words.length).join(" "));
			}
			out.push(seg);
		}
		return out;
	}

	// First whitespace-delimited word containing an in-word slash ("to/at", "myself/yourself")
	// expands to one alternative per part; recursion covers a second slashed word. The string it
	// split is KEPT alongside the parts — «сгорать/сгореть» is what the card displays, so typing it
	// verbatim has to grade right (expandOne's "verbatim first" contract). It stays first so
	// AnswerDiff still diffs a wrong answer against the curated wording.
	function expandTokenSlash(s) {
		var words = s.split(" ");
		for (var i = 0; i < words.length; i++) {
			if (words[i].indexOf("/") > 0 && words[i].indexOf("(") < 0) {
				var parts = words[i].split("/");
				var out = [];
				for (var p = 0; p < parts.length; p++) {
					if (!parts[p]) continue;
					var w = words.slice();
					w[i] = parts[p];
					out = out.concat(expandTokenSlash(w.join(" ")));
				}
				if (out.length) return [s].concat(out);
			}
		}
		return [s];
	}

	// A parenthetical whose whole content is ENDING-shaped is a compaction of the word right in
	// front of it, not a qualifier: «видел(а/о)» is «видела»/«видело», «мыть(ся)» is «мыться».
	// Returns the parts when every one of them passes the SAME shape test the "-а" segments use
	// (ENDING_SEG, minus its leading hyphen), null otherwise — so «(фейерверк)», «(чего-либо
	// силой)» and «(разг.)» fall through to the qualifier handling untouched.
	function endingParts(content) {
		var parts = content.split(/\s*\/\s*|\s*,\s*/);
		for (var i = 0; i < parts.length; i++) {
			if (!ENDING_SEG.test("-" + parts[i])) return null;
		}
		return parts;
	}

	// One variant per ending, attached to the last word of the head via the same attachEnding
	// that serves the "-а" segments — so the -ый/-ий/-ой adjective REPLACEMENT is inherited here
	// too. Nothing to attach to (the string opens with the group) yields nothing.
	function attachedEndingVariants(head, parts, tail) {
		var words = collapse(head).split(" ");
		var last = words[words.length - 1];
		if (!last) return [];
		var out = [];
		for (var i = 0; i < parts.length; i++) {
			var w = words.slice();
			w[w.length - 1] = attachEnding(last, parts[i]);
			out.push(collapse(w.join(" ") + " " + tail));
		}
		return out;
	}

	// Parenthetical qualifier variants: keep the original, add the group inlined (one variant
	// per comma-separated item inside it: "to take away (speech, breath, appetite)" gives all
	// three), add the ending-attached forms when the content is ending-shaped, and the group
	// dropped entirely ("to take away"). One group at a time; recursion covers a second group.
	function expandParens(s) {
		var m = /^(.*?)\(([^()]*)\)(.*)$/.exec(s);
		if (!m) return [s];
		var head = m[1], content = m[2], tail = m[3];
		var variants = [s];
		var items = content.split(",");
		for (var i = 0; i < items.length; i++) {
			var item = collapse(items[i]);
			if (item) variants.push(collapse(head + " " + item + " " + tail));
		}
		var endings = endingParts(content);
		if (endings) {
			var attached = attachedEndingVariants(head, endings, tail);
			for (var e = 0; e < attached.length; e++) variants.push(attached[e]);
		}
		var dropped = collapse(head + " " + tail);
		if (dropped) variants.push(dropped);
		var out = [];
		for (var v = 0; v < variants.length; v++) {
			// only recurse into derived variants (the original is final as-is)
			out = out.concat(v === 0 ? [variants[v]] : expandParens(variants[v]));
		}
		return out;
	}

	// The language's optional leading markers, applied in order and chained: for English
	// "to the trains" -> "the trains" (pattern 1) -> "trains" (pattern 2, over what 1 produced).
	// A language with no patterns gets its string back untouched.
	function expandLeadingMarkers(s, rules) {
		var patterns = rules.optionalLeading || [];
		var out = [s];
		for (var p = 0; p < patterns.length; p++) {
			for (var i = 0, n = out.length; i < n; i++) {
				var m = patterns[p].exec(out[i]);
				if (m) out.push(m[1]);
			}
		}
		return out;
	}

	// One stored translation -> every accepted spelling, verbatim first (AnswerDiff.closest
	// prefers earlier forms on ties, and the reveal should diff against the curated wording).
	function expandOne(t, rules) {
		// Comma spacing is normalized first: curated translations write ", " but a legacy
		// definition string often writes "a,b", and the depth-0 splitter matches the exact
		// separator. Inside parens this changes nothing — expandParens splits on "," itself.
		// Semicolons are group separators of the same rank as " / " («прописывал, -а; назначал,
		// -а» — two verbs, each with its own endings), so they split FIRST, or the second group's
		// endings would attach across the boundary.
		var alts = [];
		var slashSegs = [];
		topLevelSplit(t.replace(/,\s*/g, ", "), "; ").forEach(function (g) {
			slashSegs = slashSegs.concat(topLevelSplit(g, " / "));
		});
		for (var i = 0; i < slashSegs.length; i++) {
			var seg = collapse(slashSegs[i]);
			if (!seg) continue;
			var commaAlts = completeWithTail(expandEndings(topLevelSplit(seg, ", ")));
			for (var j = 0; j < commaAlts.length; j++) {
				alts = alts.concat(expandTokenSlash(commaAlts[j]));
			}
		}
		var out = [];
		for (var a = 0; a < alts.length && out.length < MAX_FORMS_PER_TRANSLATION; a++) {
			var withParens = expandParens(alts[a]);
			for (var p = 0; p < withParens.length && out.length < MAX_FORMS_PER_TRANSLATION; p++) {
				var withMarkers = expandLeadingMarkers(withParens[p], rules);
				for (var v = 0; v < withMarkers.length && out.length < MAX_FORMS_PER_TRANSLATION; v++) {
					var f = collapse(withMarkers[v]);
					if (f) out.push(f);
				}
			}
		}
		return out.length ? out : [t];
	}

	// The accepted-forms list for a reverse card: every expansion of every translation the
	// TERM carries (all senses — the bare-word cue can't say which meaning is asked, so a
	// sibling sense's translation is a right answer too), under the answer language's rules.
	// Deduped on key(); order preserved (first translation's verbatim form first).
	function accepted(translations, lang) {
		var rules = rulesFor(lang);
		var out = [], seen = {};
		for (var i = 0; i < (translations || []).length; i++) {
			var t = translations[i] == null ? "" : collapse(String(translations[i]));
			if (!t) continue;
			var forms = expandOne(t, rules);
			for (var j = 0; j < forms.length; j++) {
				var k = key(forms[j], lang);
				if (k && !seen[k]) {
					seen[k] = true;
					out.push(forms[j]);
				}
			}
		}
		return out;
	}

	// True when the attempt matches any accepted form under the reverse rules.
	function matches(forms, attempt, lang) {
		var ak = key(attempt, lang);
		if (!ak) return false;
		for (var i = 0; i < forms.length; i++) {
			if (key(forms[i], lang) === ak) return true;
		}
		return false;
	}

	// Every meaning the WHOLE term carries, in display order — the pool `accepted()` runs on, and
	// the one place the sense's `untaught` list is read: a demoted translation (changeset 0049) is
	// hidden from every display, but it is still a correct answer, so typing it must still be right.
	//
	// A definition with NO senses falls back to its own definition text — the same
	// comma-separated meaning list the dictionary displays for it, and the same fallback the
	// FORWARD cue already makes (training_session.js getMeaningCueText). That fallback is the
	// whole reason a pair that was never enriched can be drilled in reverse at all: reverse needs
	// meanings to accept, and every drillable pair has meanings, rich or plain.
	function translationsOf(defCluster) {
		var out = [];
		var defs = (defCluster && defCluster.definitionList) || [];
		for (var i = 0; i < defs.length; i++) {
			var groups = defs[i].senseGroups || [];
			for (var j = 0; j < groups.length; j++) {
				var ts = (groups[j].translations || []).concat(groups[j].untaught || []);
				for (var m = 0; m < ts.length; m++) {
					out.push(ts[m]);
				}
			}
			if (!groups.length && defs[i].definition) {
				out.push(defs[i].definition);
			}
		}
		return out;
	}

	return {accepted: accepted, key: key, matches: matches, translationsOf: translationsOf};
})();
